import { Trans } from '@lingui/macro'
import { Box, Button, Grid, IconButton, Typography } from '@mui/material'
import {
  getConfigurationVariables,
  saveConfigurationsWithUpdateByFlow
} from 'app/services/sfAuth/sfData/sfVariables'
import Loading from 'egret/components/EgretLoadable/Loading'
import { FieldArray, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { setConfigurationData } from 'app/redux/actions/ConfigurationActions'
import { getFormPages } from 'app/services/sfAuth/sfData/sfForms'
import { TooltipLabelIcon } from '../common-components/TooltipLabelIcon'
import { TextFieldWithFormik } from '../common-components/with-formik/TextFieldWithFormik'
import { FormSelectField } from './funding-streams/ConfigureFundingStreams'
import { PortalTabHeader } from '../common/PortalTabHeader'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { FormTopLabel } from '../common-components/forms/FormTopLabel'

export const sfValidation = Yup.string()
  .nullable()
  .matches(/^([a-zA-Z0-9]{15}|[a-zA-Z0-9]{18})$/, {
    message: <Trans>You must provide a valid Salesforce id</Trans>
  })

export const basePortalConfig = {
  AVAILABLE_APPLICATIONS: {
    type: 'object'
  },
  FORM_PREVIEW_CONFIG: {
    type: 'object'
  },
  FORM_USER_PROFILE: {
    type: 'formId',
    label: <Trans>User profile form</Trans>
  },
  FORM_FEEDBACK: {
    type: 'formId',
    label: <Trans>PORTAL_CONFIGURATIONS_FEEDBACK_FORM_LABEL</Trans>
  },
  FORM_RESOURCE_FEEDBACK: {
    type: 'formId',
    label: <Trans>PORTAL_CONFIGURATIONS_RESOURCE_FEEDBACK_FORM_LABEL</Trans>
  },
  FORM_ORGANIZATION_DETAILS: {
    type: 'formId',
    label: <Trans>Organization details form</Trans>
  },
  EXTERNAL_REVIEWER_PROFILE: {
    type: 'formId',
    label: <Trans>External reviewer profile form</Trans>
  },
  VIVRE_EN_VILLE_RENT_PREVIEW: {
    type: 'formId',
    label: <Trans>Vivre En Ville Rent preview form</Trans>
  },
  VIVRE_EN_VILLE_RENT_EDIT: {
    type: 'formId',
    label: <Trans>Vivre En Ville Rent edit form</Trans>
  },
  E_SAT_SURVEY: {
    type: 'id',
    label: <Trans>ESAT survey id</Trans>
  },
  SAT_COOPS_SURVEY: {
    type: 'id',
    label: <Trans>SAT (co-ops) survey id</Trans>
  },
  SAT_OSBL_SURVEY: {
    type: 'id',
    label: <Trans>SAT (osbl) survey id</Trans>
  },
  SAT_SURVEY: {
    type: 'id',
    label: <Trans>SAT survey id</Trans>
  },
  EXTERNAL_REVIEW_CBTI: {
    type: 'id',
    label: (
      <Trans>
        External review survey id for Community-Based Tenant Initiative Fund
      </Trans>
    )
  },
  EXTERNAL_REVIEW_FCHI: {
    type: 'id',
    label: <Trans>External review survey id for TRA</Trans>
  },
  EXTERNAL_REVIEW_STF_LP: {
    type: 'id',
    label: (
      <Trans>
        External review survey id for Sector Transformation Fund – Local Project
      </Trans>
    )
  },
  EXTERNAL_REVIEW_STF_SI: {
    type: 'id',
    label: (
      <Trans>
        External review survey id for Sector Transformation Fund – Sectoral
        Impact
      </Trans>
    )
  }
}

const editableConfigs = Object.entries(basePortalConfig).filter(
  ([key, value]) => value.type !== 'object'
)

export default function ConfigurePortalConfigurations () {
  const configuration = useSelector(state => state.configuration)
  const user = useSelector(state => state.user)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [nameToSfId, setNameToSfId] = useState({})
  const [initialValues, setInitialValues] = useState({})
  const [formsData, setFormsData] = React.useState([])
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    setLoading(true)
    Promise.all([getConfigurationVariables(), getFormPages()]).then(
      ([configurationObjects, forms]) => {
        const nameToSfId = {}
        configurationObjects.forEach(obj => {
          if (obj.Name) {
            nameToSfId[obj.Name] = obj.Id
          }
        })
        const initial = {}
        editableConfigs.forEach(([key, value]) => {
          initial[key] = configuration[key] || value.defaultValue || ''
        })
        setNameToSfId(nameToSfId)

        const formsCount = {}
        forms.forEach(form => {
          if (form.origin) {
            if (formsCount[form.origin]) {
              formsCount[form.origin].push(form)
            } else {
              formsCount[form.origin] = [form]
            }
          }
        })
        setFormsData(
          forms
            .map(obj => {
              let name = obj.name[user.language]
              if (obj.config.version && formsCount[obj.origin]) {
                name += ' [v.' + obj.config.version + ']'
              }
              if (obj.comments) {
                name += ' (' + obj.comments + ')'
              }
              return {
                name,
                id: obj.id
              }
            })
            .filter(name => name)
            .sort((a, b) => String(a.name).localeCompare(b.name))
        )
        setInitialValues(initial)
        setLoading(false)
      }
    )
  }, [configuration, user.language])

  if (loading) {
    return <Loading />
  }

  return (
    <Box className='portal-tab-container'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          FORM_ORGANIZATION_DETAILS: sfValidation,
          FORM_USER_PROFILE: sfValidation,
          EXECUTIVE_SUMMARY_FORM: Yup.object({
            firstReview: sfValidation,
            inDepth: sfValidation
          })
        })}
      >
        {({ isValid, values, setFieldValue }) => {
          return (
            <Grid container direction='column'>
              <PortalTabHeader
                header={<Trans>PORTAL_CONFIGURATIONS_HEADER</Trans>}
                actionButton={
                  <Button
                    variant='contained'
                    color='primary'
                    disabled={saving}
                    onClick={e => {
                      setSaving(true)
                      saveConfigurationsWithUpdateByFlow(
                        editableConfigs.map(([key, value]) => ({
                          Id: nameToSfId[key],
                          Name: key,
                          Value__c:
                            typeof values[key] === 'string'
                              ? values[key]
                              : JSON.stringify(values[key])
                        }))
                      ).then(
                        result => {
                          setSaving(false)
                          enqueueSnackbar(<Trans>Configurations saved</Trans>, {
                            variant: 'success'
                          })
                          const toSet = { ...configuration }
                          editableConfigs.forEach(([key, value]) => {
                            toSet[key] = values[key]
                          })
                          dispatch(setConfigurationData(toSet))
                        },
                        reject => {
                          setSaving(false)
                          enqueueSnackbar(
                            <Trans>Error ocurred while saving</Trans>,
                            {
                              variant: 'error'
                            }
                          )
                        }
                      )
                    }}
                    startIcon={<SaveIcon />}
                  >
                    <Trans>Save</Trans>
                  </Button>
                }
              />
              {editableConfigs.map(([key, value]) => {
                let element = null

                if (value.type === 'id') {
                  element = (
                    <div key={key}>
                      <Grid item>
                        <TextFieldWithFormik
                          name={key}
                          variant='outlined'
                          fullWidth
                          style={{}}
                          isTopLabelSmall={false}
                          topLabel={
                            <>
                              {key === 'GRANTEE_REPORT_FINAL' && (
                                <Box>
                                  <Trans>Portal surveys</Trans>
                                </Box>
                              )}
                              {value.label}
                            </>
                          }
                        />
                      </Grid>
                    </div>
                  )
                }

                if (value.type === 'formId') {
                  element = (
                    <Box
                      key={key}
                      sx={{ marginBottom: '10px' }}
                    >
                      <FormSelectField
                        isTopLabel
                        key={key}
                        formsData={formsData}
                        name={key}
                        label={value.label}
                      />
                    </Box>
                  )
                }

                if (value.type === 'objectsOrder') {
                  const order = values[key] || value.defaultValue
                  element = (
                    <Grid key={key}>
                      <div>
                        <FormTopLabel>
                          {value.label}{' '}
                        </FormTopLabel>
                        <TooltipLabelIcon
                          tooltip={
                            <Trans>
                              Order of objects must match the one configured in
                              related form
                            </Trans>
                          }
                        />
                      </div>
                      {/* <TextFieldWithFormik
                        name={`${key}.id`}
                        variant='outlined'
                        fullWidth
                        // label={value.label}
                      /> */}
                      <div>
                        {/* <Trans>Form objects order</Trans>
                        <TooltipLabelIcon
                          tooltip={
                            <Trans>
                              Order of objects must match the one configured in
                              selected form
                            </Trans>
                          }
                        /> */}
                        {order.map((subKey, index) => (
                          <Grid container key={index} xs alignItems='center'>
                            <FormTopLabel>
                              {String(index + 1) + '. '}
                              {value.objects[subKey]?.label}
                            </FormTopLabel>
                            <IconButton
                              size='small'
                              disabled={index === 0}
                              onClick={e => {
                                const toSet = [...order]
                                const toMove = toSet[index]
                                const toReplace = toSet[index - 1]
                                toSet[index - 1] = toMove
                                toSet[index] = toReplace
                                setFieldValue(`${key}`, toSet)
                              }}
                            >
                              <ArrowUpwardIcon />
                            </IconButton>
                            <IconButton
                              size='small'
                              disabled={index === order.length - 1}
                              onClick={e => {
                                const toSet = [...order]
                                const toMove = toSet[index]
                                const toReplace = toSet[index + 1]
                                toSet[index + 1] = toMove
                                toSet[index] = toReplace
                                setFieldValue(`${key}`, toSet)
                              }}
                            >
                              <ArrowDownwardIcon />
                            </IconButton>
                          </Grid>
                        ))}
                      </div>
                    </Grid>
                  )
                }

                if (value.type === 'ids') {
                  element = (
                    <FieldArray
                      key={key}
                      name={key}
                      render={({ push, remove }) => {
                        const array = values[key] || []
                        return (
                          <Grid
                            item
                            container
                            direction='column'
                          >
                            <div>
                              {value.label}{' '}
                              {value.tooltip && (
                                <TooltipLabelIcon tooltip={value.tooltip} />
                              )}
                              <IconButton
                                size='small'
                                onClick={e => {
                                  push('')
                                }}
                              >
                                <AddIcon />
                              </IconButton>
                            </div>
                            {array.map((id, index) => {
                              return (
                                <Grid
                                  item
                                  key={index}
                                  xs
                                  container
                                  wrap='nowrap'
                                  alignItems='center'
                                >
                                  <TextFieldWithFormik
                                    style={{}}
                                    isTopLabelSmall={false}
                                    name={`${key}.${index}`}
                                    variant='outlined'
                                    fullWidth
                                    topLabel={
                                      Number(index + 1) + '.'
                                    }
                                    // label={obj.label}
                                  />
                                  <IconButton
                                    onClick={e => {
                                      remove(index)
                                    }}
                                    size='large'
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              )
                            })}
                          </Grid>
                        )
                      }}
                    />
                  )
                }
                if (element == null) {
                  return null
                }
                return (
                  <Box key={key} sx={{ marginBottom: '10px' }}>
                    {element}
                  </Box>
                )
              })}
            </Grid>
          )
        }}
      </Formik>
    </Box>
  )
}
